import { createContext, useContext, useState, useEffect, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "wouter";
import {
  AVAILABLE_LANGUAGES,
  getLanguageFromPath,
  localizePath,
  stripLanguageFromPath,
  type LanguageCode,
} from "@/site-config";
import { ensureLanguageLoaded } from "@/i18n-resources";

interface LanguageContextType {
  currentLanguage: LanguageCode;
  changeLanguage: (lang: LanguageCode) => void;
  availableLanguages: readonly LanguageCode[];
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export function LanguageProvider({
  children,
  initialLanguage = "en",
}: {
  children: ReactNode;
  initialLanguage?: LanguageCode;
}) {
  const { i18n } = useTranslation();
  const [location, setLocation] = useLocation();
  const [currentLanguage, setCurrentLanguage] = useState<LanguageCode>(initialLanguage);
  const availableLanguages = AVAILABLE_LANGUAGES;

  useEffect(() => {
    const languageFromPath = getLanguageFromPath(location);

    if (languageFromPath !== currentLanguage) {
      setCurrentLanguage(languageFromPath);
    }

    if (i18n.language !== languageFromPath) {
      void ensureLanguageLoaded(i18n, languageFromPath).then(() => i18n.changeLanguage(languageFromPath));
    }

    if (typeof document !== "undefined") {
      document.documentElement.lang = languageFromPath;
    }
  }, [location, currentLanguage, i18n]);

  useEffect(() => {
    if (i18n.language !== initialLanguage) {
      void ensureLanguageLoaded(i18n, initialLanguage).then(() => i18n.changeLanguage(initialLanguage));
    }
  }, [i18n, initialLanguage]);

  const changeLanguage = (lang: LanguageCode) => {
    if (!availableLanguages.includes(lang)) return;

    void ensureLanguageLoaded(i18n, lang).then(() => i18n.changeLanguage(lang));
    setCurrentLanguage(lang);

    if (typeof document !== "undefined") {
      document.documentElement.lang = lang;
    }

    const basePath = stripLanguageFromPath(location);
    setLocation(localizePath(basePath, lang));

    if (typeof window !== "undefined") {
      window.localStorage.setItem("preferredLanguage", lang);
    }
  };

  return (
    <LanguageContext.Provider value={{ currentLanguage, changeLanguage, availableLanguages }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}
